import React from 'react';
import useResponsive from '../hooks/useResponsive';
import { getGradeColor } from '../../../utils/gradeColors';

const SubjectCard = ({
  subject,
  subjectGrades,
  average,
  isAuthenticated,
  user,
  onAddGrade,
  onEditGrade,
  onDeleteGrade,
  onRemoveSubject,
  onGradeMouseEnter,
  onGradeMouseLeave,
  onGradeMouseMove,
  canRemoveSubject
}) => {
  const { isMobile } = useResponsive();

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300 group h-full flex flex-col">
      {/* Card Content - Flex grow to fill available space */}
      <div className="flex-grow flex flex-col">
        {/* Subject Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h4 className="text-base sm:text-lg font-bold text-grey-dusk group-hover:text-sunrise-red transition-colors duration-300 leading-tight">
                  {subject.subjectCode}
                </h4>
                <h4 className="text-base sm:text-lg font-bold text-grey-dusk group-hover:text-sunrise-red transition-colors duration-300 leading-tight">
                  {subject.name}
                </h4>
                {subject.description && (
                  <p className="text-gray-600 text-xs sm:text-sm mt-1 line-clamp-2">
                    {subject.description}
                  </p>
                )}
              </div>
              {isAuthenticated && user && (
                <button
                  onClick={() => onRemoveSubject(subject)}
                  disabled={!canRemoveSubject}
                  className={`ml-2 p-1.5 rounded-lg transition-all duration-200 flex-shrink-0 ${
                    !canRemoveSubject
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-400 hover:text-sunrise-red hover:bg-sunrise-red/10"
                  }`}
                  title={
                    !canRemoveSubject
                      ? "Cannot remove the last subject from semester"
                      : "Remove subject"
                  }
                >
                  <i className="fas fa-times text-sm"></i>
                </button>
              )}
            </div>
          </div>

          {/* Average Grade */}
          <div className="ml-2 sm:ml-4 text-center flex-shrink-0">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              {isMobile ? 'Avg' : 'Average'}
            </div>
            {average ? (
              <div
                className="text-lg sm:text-2xl font-bold px-2 sm:px-3 py-1 rounded-lg text-white"
                style={{ backgroundColor: getGradeColor(average) }}
              >
                {average}
              </div>
            ) : (
              <div className="text-lg sm:text-xl text-gray-400 font-medium">
                -
              </div>
            )}
          </div>
        </div>

        {/* Individual Grades - Flex grow to take available space */}
        <div className="flex-grow mb-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
            Individual Grades
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {subjectGrades.map((grade, index) => {
              return (
                <div key={index} className="group relative">
                  <div
                    className="px-2 sm:px-3 py-2 rounded-lg text-sm font-medium border cursor-pointer hover:scale-105 hover:shadow-lg transition-all duration-200 relative text-white"
                    style={{ 
                      backgroundColor: getGradeColor(grade.gradeValue),
                      borderColor: getGradeColor(grade.gradeValue)
                    }}
                    onMouseEnter={(e) => onGradeMouseEnter(grade, e)}
                    onMouseLeave={onGradeMouseLeave}
                    onMouseMove={onGradeMouseMove}
                  >
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <span className="font-semibold text-xs sm:text-sm">{grade.gradeValue}</span>
                      <span className="text-xs opacity-75">
                        <i className="fas fa-info-circle"></i>
                      </span>
                    </div>
                    
                    {/* Action buttons - only show for authenticated users */}
                    {isAuthenticated && user && (
                      <div className={`absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1 ${isMobile ? 'opacity-100' : ''}`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditGrade(grade);
                          }}
                          className="w-4 h-4 sm:w-5 sm:h-5 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center text-xs transition-colors duration-200 shadow-lg"
                          title="Edit grade"
                        >
                          <i className="fas fa-pencil-alt text-xs"></i>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteGrade(grade);
                          }}
                          className="w-4 h-4 sm:w-5 sm:h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs transition-colors duration-200 shadow-lg"
                          title="Delete grade"
                        >
                          <i className="fas fa-trash text-xs"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {subjectGrades.length === 0 && (
              <div className="text-gray-400 text-sm italic">
                No grades yet
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Grade Button - Always at bottom */}
      <div className="pt-4 border-t border-gray-100 mt-auto">
        <button
          onClick={() => onAddGrade(subject.id)}
          className="w-full bg-gradient-to-r from-green-cloud to-green-cloud/80 text-white font-medium py-2 sm:py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 hover:from-green-cloud/90 hover:to-green-cloud/70 text-sm sm:text-base"
        >
          <i className="fas fa-plus"></i>
          <span>Add Grade</span>
        </button>
      </div>
    </div>
  );
};

export default SubjectCard;